import React, { useCallback, useEffect } from 'react';
import { string, arrayOf, shape, node, bool, number, func } from 'prop-types';
import { withStyles, makeStyles } from '@mui/styles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useLocation } from 'react-router-dom';
import noop from 'lodash/noop';

import THEME from '../../../constants/theme';

const TabsStyled = withStyles({
  root: {
    padding: 0,
    margin: '0 0 16px',
    borderBottom: '1px solid #E6E9EC',
    backgroundColor: '#fff',
    width: '100%',
    position: 'relative',
  },
  indicator: {
    height: '2px',
    bottom: '0px',
    backgroundColor: THEME.primaryColors.primary,
  },
})(Tabs);

const TabStyled = withStyles((theme) => ({
  root: {
    padding: '8px 0 8px 0px',
    margin: 0,
    textTransform: 'none',
    height: 56,
    minWidth: 48,
    marginRight: theme.spacing(3), //24px
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: 'unset',
    transition: 'all 0.3s ease-out',
    color: THEME.greyColors.grey1,
    '&>span': {
      alignItems: 'flex-start',
    },
    '&:hover': {
      opacity: 1,
      '&>span': {
        color: `${THEME.primaryColors.primary} !important`,
      },
    },
    '&:focus': {
      color: THEME.primaryColors.primary,
    },
  },
  selected: {
    color: THEME.primaryColors.primary,
  },
  disabled: {
    color: THEME.primaryColors.blackMain,
  },
}))(Tab);
const TabPanel = ({ children, value, index, ...other }) => (
  <Typography
    component="div"
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </Typography>
);

TabPanel.propTypes = {
  children: node,
  index: number.isRequired,
  value: number.isRequired,
};

TabPanel.defaultProps = {
  children: null,
};

/**
 * @param {number} i
 * */
const a11yProps = (i) => ({
  id: `simple-tab-${i}`,
  ariaControls: `simple-tabpanel-${i}`,
});

const SimpleTabsStyles = makeStyles(() => ({
  root: {
    padding: 0,
    margin: 0,
    boxShadow: 'none',
    width: '100%',
    backgroundColor: 'transparent!important',
  },
  head: {
    backgroundColor: 'transparent!important',
  },
  body: {
    backgroundColor: 'transparent!important',
  },
}));

const SimpleTabs = ({ tabs, color, centered, initialValue, onChange, setTabValue, tabValue }) => {
  const classes = SimpleTabsStyles();
  const [value, setValue] = React.useState(initialValue);

  const customTabValue = tabValue ?? value;

  const handleSetValue = useCallback(
    (newValue) => {
      if (typeof setTabValue === 'function') {
        setTabValue(newValue);
        return;
      }

      setValue(newValue);
    },
    [setTabValue]
  );

  const location = useLocation();
  useEffect(() => {
    if (location?.state?.defaultTab) {
      handleSetValue(location?.state?.defaultTab);
    }
  }, [location?.state, handleSetValue]);

  const handleChange = useCallback(
    (_, newValue) => {
      handleSetValue(newValue);
      onChange(tabs[newValue]?.id);
    },
    [onChange, tabs, handleSetValue]
  );
  return (
    <div className={classes.root}>
      <TabsStyled
        color={color}
        className={classes.head}
        centered={centered}
        value={customTabValue}
        onChange={handleChange}
        aria-label="simple tabs"
      >
        {tabs.map(({ label, id, disabled }, i) => {
          const { id: _id, ariaControls } = a11yProps(i);

          return (
            <TabStyled
              color={color}
              key={label}
              label={label}
              id={id || _id}
              aria-controls={ariaControls}
              disabled={!!disabled}
            />
          );
        })}
      </TabsStyled>

      {tabs.map(({ label, node: childNode }, i) => (
        <TabPanel className={classes.body} key={label} value={customTabValue} index={i}>
          {childNode}
        </TabPanel>
      ))}
    </div>
  );
};

SimpleTabs.propTypes = {
  tabs: arrayOf(
    shape({
      label: string.isRequired,
      node: node,
    }).isRequired
  ).isRequired,
  color: string,
  centered: bool,
  initialValue: number,
  onChange: func,
  tabValue: number,
  setTabValue: func,
};

SimpleTabs.defaultProps = {
  color: undefined,
  centered: false,
  initialValue: 0,
  onChange: noop,
  tabValue: null,
  setTabValue: null,
};

export default SimpleTabs;
export { TabsStyled, TabStyled, TabPanel, SimpleTabsStyles };
